// @ts-expect-error grammar.js is generated by Jison and has no types
import { parser } from './grammar.js';
import type { IRPattern, IRExpression, IRCounter, IRType, IRStep } from '../types/ir';

interface YY {
  id: () => string;
  unquote: (s: string) => string;
  bin: (op: string, left: IRExpression, right: IRExpression) => IRExpression;
  defaultFor: (type: IRType) => IRExpression;
  splitInlineCounters: (steps: any[]) => { counters: IRCounter[]; body: IRStep[] };
}

let nodeCounter = 0;
const yy: YY = {
  id() {
    nodeCounter += 1;
    return `n_${nodeCounter.toString(36)}_${crypto.randomUUID().slice(0, 8)}`;
  },

  unquote(s: string) {
    return s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\').replace(/\\n/g, '\n');
  },

  bin(op: any, left: IRExpression, right: IRExpression): IRExpression {
    return { kind: 'binop', op, left, right };
  },

  defaultFor(type: IRType): IRExpression {
    if (type === 'int') return { kind: 'literal', type: 'int', value: 0 };
    if (type === 'text') return { kind: 'literal', type: 'text', value: '' };
    if (type === 'bool') return { kind: 'literal', type: 'bool', value: false };
    throw new Error(`Unknown type: ${type}`);
  },

  splitInlineCounters(steps: any[]) {
    const counters: IRCounter[] = [];
    const body: IRStep[] = [];
    for (const s of steps) {
      if (s.kind === '_inline_counter') {
        counters.push(s.decl);
      } else {
        body.push(s);
      }
    }
    return { counters, body };
  },
};

(parser as any).yy = yy;

export type ParseContext = 'wizard' | 'builder' | 'import' | 'load';

export function parseDSL(source: string, opts?: { context?: ParseContext }): IRPattern {
  try {
    const raw = parser.parse(source) as IRPattern;

    // Post-parse cleanup
    const pattern = finalizePattern(raw);

    // Reference resolution
    resolveReferences(pattern);

    // Validation
    validatePattern(pattern);

    return pattern;
  } catch (err) {
    // Classify into error_class.
    // The Jison parser throws generic Error objects — we default to 'parse'.
    // Validation errors thrown by validatePattern or resolveReferences are
    // distinguishable by message prefix in the future; defaulting to 'parse'
    // for now. TODO: refine classification once Jison error types are known.
    let error_class: 'lex' | 'parse' | 'reference' | 'validation' = 'parse';
    if (err instanceof Error) {
      const msg = err.message.toLowerCase();
      if (msg.includes('lexical') || msg.includes('unexpected character') || msg.includes('lex')) {
        error_class = 'lex';
      } else if (msg.includes('reference') || msg.includes('undefined') || msg.includes('not found')) {
        error_class = 'reference';
      } else if (msg.includes('validation') || msg.includes('must have') || msg.includes('duplicate')) {
        error_class = 'validation';
      }
    }
    // Dynamic import: parser ships as a shared chunk, and a static import of
    // analytics/posthog from here creates a parser→entry chunk-graph cycle
    // that adds a stray modulepreload to index.html (+~150 ms render-blocking
    // under Lighthouse simulate). See docs/perf-findings.md.
    void import('../analytics/posthog').then(({ track }) =>
      track('dsl_parse_failed', { error_class, context: opts?.context ?? 'load' }),
    );
    throw err;
  }
}

function finalizePattern(pattern: any): IRPattern {
  // Deep walk to fix the ambiguous counts and other placeholders
  walkNodes(pattern, (node) => {
    if (node.kind === '_count_until_ambiguous') {
      const expr = node.expr as IRExpression;
      // Heuristic for now: if it's a literal string, it's a text goal.
      // Otherwise it's a predicate.
      if (expr.kind === 'literal' && expr.type === 'text') {
        Object.assign(node, { kind: 'count_until_text', text_expr: expr });
        delete (node as any).expr;
      } else {
        Object.assign(node, { kind: 'count_until_pred', predicate: expr });
        delete (node as any).expr;
      }
    }
  });
  return pattern as IRPattern;
}

function walkNodes(node: any, callback: (node: any) => void) {
  if (!node || typeof node !== 'object') return;
  callback(node);
  for (const key in node) {
    if (Array.isArray(node[key])) {
      node[key].forEach((child: any) => walkNodes(child, callback));
    } else if (typeof node[key] === 'object') {
      walkNodes(node[key], callback);
    }
  }
}

function resolveReferences(pattern: IRPattern) {
  // TODO: Implement full scope-based resolution.
  // For now, we'll just rewrite counter_ref to param_ref if it matches a param name.
  const paramNames = new Set(pattern.params.map((p) => p.name));

  walkNodes(pattern, (node) => {
    if (node.kind === 'counter_ref') {
      if (paramNames.has(node.name)) {
        node.kind = 'param_ref';
      }
    }
  });
}

function validatePattern(pattern: IRPattern) {
  // Basic validation
  if (pattern.segments.length === 0) {
    throw new Error('Pattern must have at least one segment.');
  }
  // TODO: Add cycle detection and type checking.
}
